import React, { useState, useEffect } from 'react';

import { Formik, Form } from 'formik';
import styled from 'styled-components';

import { default as AntDrawer } from 'components/ui/Drawer';
import DrawerFooter from 'components/ui/DrawerFooter';
import DrawerBody from 'components/ui/DrawerBody';
import CtaPrimary from 'components/ui/CtaPrimary';
import DrawerSuccessMessages from 'components/ui/DrawerSuccessMessages';

import { getTranslatedString } from 'helpers/i18n';
import { InternalComments } from './InternalComments';
import { LinkServiceRequests } from './LinkServiceRequests';
import { LinkProject } from './LinkProject';
import { ViewPurchaseOrderItemsList } from './ViewPurchaseOrderItemsList';
import { EditRequesterPurchaseOrderDrawer } from './EditRequesterPurchaseOrderDrawer';
import { ViewPurchaseOrderHeader } from './shared/ViewPurchaseOrderHeader';
import { ViewPurchaseOrderDetails } from './shared/ViewPurchaseOrderDetails';

import Lottie from 'react-lottie';
import errorAnimationData from 'assets/error.json';
import loadingAnimationData from 'assets/loading.json';
import successAnimationData from 'assets/success.json';

const StyledViewOuterContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: ${props => (props.readOnly ? 'start' : 'space-between')};
`;

const UNAPPROVED_STATE = 'unapproved_state';
const APPROVED_STATE = 'approved_state';
const DENIED_STATE = 'denied_state';
const SUCCESS_APPROVAL_PAGE = 'success_approval_page';

export const RequesterViewPurchaseOrder = ({
  purchaseOrder,
  toggleViewPurchaseOrder,
  createPurchaseOrderComment,
  getPurchaseOrder,
  getPurchaseOrderStats,
  fetchPurchaseOrders,
  getUnits,
  attachPurchaseOrderServiceRequest,
  removePurchaseOrderServiceRequest,
  postProject,
  attachPurchaseOrderProject,
  removePurchaseOrderProject,
  properties,
  units,
  approvePurchaseOrder,
  downloadPdf,
}) => {
  const [viewState, setViewState] = useState(UNAPPROVED_STATE);
  const [toggleEditPurchaseOrder, setToggleEditPurchaseOrder] = useState(false);
  const [isUpdatingPurchaseOrder, setIsUpdatingPurchaseOrder] = useState(false);
  const [isUpdatingPurchaseOrderError, setIsUpdatingPurchaseOrderError] = useState(false);
  const [purchaseOrderItems, setPurchaseOrderItems] = useState([]);
  const [purchaseOrderItemsModified, setPurchaseOrderItemsModified] = useState(false);

  useEffect(
    () => {
      if (purchaseOrder.purchase_order_items) {
        setPurchaseOrderItems(purchaseOrder.purchase_order_items);
        if (
          purchaseOrder.purchase_order_items.some(
            item =>
              ['modified', 'newly_added'].includes(item.state) &&
              purchaseOrder.status === 'needs_review'
          )
        ) {
          setPurchaseOrderItemsModified(true);
        }
      }
      switch (purchaseOrder.status) {
        case 'approved':
          setViewState(APPROVED_STATE);
          break;
        case 'denied':
          setViewState(DENIED_STATE);
          break;
        default:
          break;
      }
    },
    [
      purchaseOrder.purchase_order_items,
      setPurchaseOrderItems,
      setPurchaseOrderItemsModified,
      purchaseOrder.status,
    ]
  );

  const handleApproval = () => {
    setIsUpdatingPurchaseOrder(true);
    approvePurchaseOrder({}, purchaseOrder.id)
      .then(res => {
        fetchPurchaseOrders({
          updateList: true,
        });
        getPurchaseOrderStats();
        setIsUpdatingPurchaseOrder(false);
        setViewState(SUCCESS_APPROVAL_PAGE);
      })
      .catch(err => {
        setIsUpdatingPurchaseOrder(false);
        setIsUpdatingPurchaseOrderError(true);
        console.error(err);
      });
  };

  const handleOpeningEditPurchaseOrder = () => {
    getUnits(purchaseOrder.property_id, { for_multiselect: true });
    setToggleEditPurchaseOrder(true);
  };

  const handleSelectServiceRequest = serviceRequest => {
    let params = {
      service_request_id: serviceRequest.id,
    };
    attachPurchaseOrderServiceRequest(params, purchaseOrder.id)
      .then(res => {
        getPurchaseOrder(purchaseOrder.id);
        fetchPurchaseOrders({
          updateList: true,
        });
      })
      .catch(err => {
        console.error(err);
      });
  };

  const handleUnlinkServiceRequest = serviceRequestId => {
    removePurchaseOrderServiceRequest(purchaseOrder.id, serviceRequestId)
      .then(res => {
        getPurchaseOrder(purchaseOrder.id);
        fetchPurchaseOrders({
          updateList: true,
        });
      })
      .catch(err => {
        console.error(err);
      });
  };

  const handleSelectProject = projectId => {
    attachPurchaseOrderProject(purchaseOrder.id, projectId)
      .then(res => {
        getPurchaseOrder(purchaseOrder.id);
      })
      .catch(err => {
        console.error(err);
      });
  };

  const handleUnlinkProject = projectId => {
    removePurchaseOrderProject(purchaseOrder.id, projectId)
      .then(res => {
        getPurchaseOrder(purchaseOrder.id);
      })
      .catch(err => {
        console.error(err);
      });
  };

  const handlePropertyChange = value => {
    getUnits(value, { for_multiselect: true });
  };

  const serviceRequestIds = purchaseOrder.maintenance_tickets.map(maintenance_ticket =>
    String(maintenance_ticket.id)
  );

  const readOnly = viewState === APPROVED_STATE || viewState === DENIED_STATE;
  if (isUpdatingPurchaseOrder) {
    return renderLoading();
  }

  if (viewState === SUCCESS_APPROVAL_PAGE) {
    return renderSuccess(toggleViewPurchaseOrder);
  }

  if (isUpdatingPurchaseOrderError) {
    return renderError(() => setIsUpdatingPurchaseOrderError(false));
  }

  return (
    <React.Fragment>
      <StyledViewOuterContainer readOnly={readOnly}>
        <ViewPurchaseOrderHeader
          purchaseOrder={purchaseOrder}
          toggleViewPurchaseOrder={toggleViewPurchaseOrder}
        />
        <DrawerBody>
          <div>
            <ViewPurchaseOrderDetails purchaseOrder={purchaseOrder} />
            <ViewPurchaseOrderItemsList
              purchaseOrderItems={purchaseOrderItems}
              readOnly={readOnly}
              handleOpeningEditPurchaseOrder={handleOpeningEditPurchaseOrder}
              purchaseOrderItemsModified={purchaseOrderItemsModified}
            />

            <AntDrawer
              maskClosable={false}
              keyboard
              visible={toggleEditPurchaseOrder}
              onClose={toggleEditPurchaseOrder}
              closable={false}
              width={600}
            >
              <EditRequesterPurchaseOrderDrawer
                purchaseOrder={purchaseOrder}
                toggleEditPurchaseOrder={setToggleEditPurchaseOrder}
                getPurchaseOrderStats={getPurchaseOrderStats}
                fetchPurchaseOrders={fetchPurchaseOrders}
              />
            </AntDrawer>
          </div>
          <LinkServiceRequests
            handleSelectServiceRequest={handleSelectServiceRequest}
            handleUnlinkServiceRequest={handleUnlinkServiceRequest}
            propertyId={purchaseOrder.property_id}
            selectedServiceRequests={serviceRequestIds}
            serviceRequests={purchaseOrder.maintenance_tickets}
          />
          <LinkProject
            handleSelectProject={handleSelectProject}
            handleUnlinkProject={handleUnlinkProject}
            selectedProject={purchaseOrder.project}
            propertyId={purchaseOrder.property_id}
            properties={properties}
            units={units}
            postProject={postProject}
            fetchUnitsOfProperty={handlePropertyChange}
            viewMode
          />
          <Formik
            initialValues={{
              internal_comments: '',
            }}
            onSubmit={(values, { resetForm }) => {
              createPurchaseOrderComment(values, purchaseOrder.id)
                .then(res => {
                  getPurchaseOrder(purchaseOrder.id);
                  resetForm();
                })
                .catch(err => {
                  console.error(err);
                });
            }}
          >
            {({ values, setFieldValue }) => {
              const handleCommentChange = event => {
                setFieldValue('internal_comments', event.currentTarget.value);
              };
              return (
                <Form>
                  <InternalComments
                    canAddNewNotes={viewState !== APPROVED_STATE}
                    disableSend={!values.internal_comments}
                    handleCommentChange={handleCommentChange}
                    comments={purchaseOrder.internal_comments}
                    values={values}
                    showOnlyComments={viewState === APPROVED_STATE}
                    noBackground
                  />
                </Form>
              );
            }}
          </Formik>
        </DrawerBody>
        {viewState === DENIED_STATE && (
          <DrawerFooter
            noAction
            descriptionTitle={getTranslatedString(
              'landlordPortal.denyPurchaseOrder.deniedFooterTitle'
            )}
            descriptionSubtitle={purchaseOrder.rejected_reason}
          />
        )}

        {viewState === UNAPPROVED_STATE &&
          purchaseOrder.status !== 'pending_approval' && (
            <DrawerFooter
              primaryActionText={getTranslatedString(
                'landlordPortal.viewPurchaseOrderDrawer.approveChanges'
              )}
              onPrimaryActionClick={handleApproval}
              topTitleText={
                purchaseOrderItemsModified
                  ? getTranslatedString(
                      'landlordPortal.viewPurchaseOrderDrawer.thereHasBeenModifications'
                    )
                  : null
              }
            />
          )}

        {viewState === APPROVED_STATE && (
          <DrawerFooter
            primaryActionText={getTranslatedString(
              'landlordPortal.viewPurchaseOrderDrawer.savePdf'
            )}
            onPrimaryActionClick={() => downloadPdf(purchaseOrder.id)}
          />
        )}
      </StyledViewOuterContainer>
    </React.Fragment>
  );
};

const renderLoading = () => {
  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: loadingAnimationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  return (
    <DrawerSuccessMessages
      headerImageSource={<Lottie options={defaultOptions} height={150} width={150} />}
      headerText={getTranslatedString('landlordPortal.addPurchaseOrderDrawer.loadingHeader')}
      bodyText={getTranslatedString('globalDrawer.loadingBody')}
    />
  );
};

const renderError = resetError => {
  const defaultOptions = {
    loop: false,
    autoplay: true,
    animationData: errorAnimationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  return (
    <DrawerSuccessMessages
      headerImageSource={<Lottie options={defaultOptions} height={150} width={150} />}
      headerText={getTranslatedString('landlordPortal.addPurchaseOrderDrawer.errorHeader')}
      bodyText={getTranslatedString('globalDrawer.errorBody')}
      cta={
        <CtaPrimary onClick={resetError}>{getTranslatedString('globalDrawer.goBack')}</CtaPrimary>
      }
    />
  );
};

const renderSuccess = handleClose => {
  const defaultOptions = {
    loop: false,
    autoplay: true,
    animationData: successAnimationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  const headerText = getTranslatedString('landlordPortal.addPurchaseOrderDrawer.successHeader');
  const bodyText = getTranslatedString('globalDrawer.successBody');

  return (
    <DrawerSuccessMessages
      headerImageSource={<Lottie options={defaultOptions} height={150} width={150} />}
      headerText={headerText}
      bodyText={bodyText}
      cta={<CtaPrimary onClick={handleClose}>{getTranslatedString('close')}</CtaPrimary>}
    />
  );
};
